var _jsxFileName = "/codebuild/output/src1806367562/src/bitbucket.org/newyuinc/adc-polaris-web/src/components/settings/AccountInformation/index.tsx";
import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { APP_RETURN_TIMEOUT, ApiStatus, ErrorCode } from 'Constants';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import { useAccount, useApiError } from 'Hooks';
import { CLOSE_MODAL, SHOW_MODAL } from 'Reducers/modal/types';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { logout } from 'Utilities/session';
import { store } from 'Utilities/store';
import { updatePatient } from 'Services/patient';
import AccountInfoAdult from './AccountInfoAdult';
import AccountInfoMinor from './AccountInfoMinor';
const mapStateToProps = ({
  auth,
  config: {
    validation,
    minAge
  },
  env: {
    osType,
    osVersion,
    app,
    appVersion
  }
}) => {
  return {
    patient: {
      ...auth.include.patient,
      phoneNumber: auth.include.patient.phoneNumber ? auth.include.patient.phoneNumber : ''
    },
    accountId: auth.account_id,
    firstNameLimit: validation.patient.firstNameMaxLength,
    lastNameLimit: validation.patient.lastNameMaxLength,
    osType,
    osVersion,
    app,
    appVersion,
    minAge
  };
};
const connector = connect(mapStateToProps);
const AccountInformation = ({
  patient,
  accountId,
  minAge,
  osType,
  osVersion,
  app,
  appVersion,
  ...rest
}) => {
  const isLLU = app === 'llu';
  const showPhoneInput = !isLLU && patient.country === 'US';
  const [loading, setLoading] = useState(false);
  const [phoneErrorTimestamp, setPhoneErrorTimestamp] = useState(0);
  const {
    validateIsMinor,
    validateDOBAndDisplayError,
    validateEntriesLengthAndDisplayError,
    showMinorRestrictionMessage
  } = useAccount();
  const isMinor = validateIsMinor(patient.dateOfBirth, minAge);
  const {
    showApiErrorModal,
    showNetworkErrorModal
  } = useApiError();
  const handleApiRequestError = useCallback(error => {
    var _error$response;
    const code = error === null || error === void 0 || (_error$response = error.response) === null || _error$response === void 0 ? void 0 : _error$response.data.code;
    switch (error.status) {
      case ApiStatus.UNAUTHORIZED:
        logout();
        break;
      case ApiStatus.BAD_REQUEST:
        {
          if (code === ApiStatus.INVALID_DATA_CODE) {
            setPhoneErrorTimestamp(Date.now());
            return;
          }
          break;
        }
      case ApiStatus.CONFLICT:
        if (code === ApiStatus.PHONE_NUMBER_ALREADY_EXISTS_CODE) {
          setPhoneErrorTimestamp(Date.now());
        }
        break;
      default:
        error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
    }
  }, [showApiErrorModal, showNetworkErrorModal]);
  const hasFormChanged = useCallback((initialValues, currentValues) => {
    return !isEqual(initialValues, currentValues);
  }, []);
  const hasChangesBesideMobileNumber = useCallback(values => {
    const newPatient = {
      ...values,
      phoneNumber: undefined
    };
    const initialValues = {
      ...patient,
      phoneNumber: undefined
    };
    return hasFormChanged(newPatient, initialValues);
  }, [hasFormChanged, patient]);
  const onSave = useCallback(values => {
    updatePatient(accountId, values, osType, osVersion, app, appVersion).then(({
      data: response
    }) => {
      const notifyFn = ({
        phoneNumber = values.phoneNumber,
        phoneVerified = false
      }) => {
        const notifyBody = {
          id: accountId,
          country: values.country,
          language: values.language,
          firstName: values.firstName,
          lastName: values.lastName,
          dateOfBirth: values.dateOfBirth,
          email: values.email,
          guardianFirstName: values.guardianFirstName,
          guardianLastName: values.guardianLastName,
          ...((phoneNumber === null || phoneNumber === void 0 ? void 0 : phoneNumber.length) && {
            phoneNumber
          })
        };
        if (phoneVerified) {
          notifyBody.phoneVerified = true;
        }
        return notifyApp('adc-webview:update-account', notifyBody);
      };
      if (!isEmpty(response)) {
        var _response$verificatio;
        const verificationTasksNotVerified = response === null || response === void 0 || (_response$verificatio = response.verificationTasks) === null || _response$verificatio === void 0 ? void 0 : _response$verificatio.filter(task => task.verified === false);
        if (verificationTasksNotVerified && (verificationTasksNotVerified === null || verificationTasksNotVerified === void 0 ? void 0 : verificationTasksNotVerified.length) > 0) {
          const verificationTask = verificationTasksNotVerified[0];
          return mediator.publish('router:navigate', {
            url: '/verify-number',
            data: {
              taskID: verificationTask.id,
              mobileNumber: verificationTask.verificationTarget,
              showCloseButton: true,
              onSuccessfulVerify: () => notifyFn({
                phoneVerified: true
              }),
              onCloseModal: () => {
                if (hasChangesBesideMobileNumber(values)) {
                  notifyFn({
                    phoneNumber: patient.phoneNumber
                  });
                } else {
                  notifyApp('adc-webview:navigate-back-app');
                }
              }
            }
          });
        }
      }
      notifyFn({});
      // This timeout is added to give the app time to redirect the user back.
      setTimeout(() => {
        setLoading(false);
      }, APP_RETURN_TIMEOUT);
    }).catch(error => {
      setLoading(false);
      handleApiRequestError(error);
    });
  }, [accountId, app, appVersion, handleApiRequestError, hasChangesBesideMobileNumber, osType, osVersion, patient.phoneNumber]);
  const showUnsavedChangesModal = () => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        header: i18n.t('AccountInformationPersonalInfo.modals.unsavedChangesError.title'),
        details: i18n.t('AccountInformationPersonalInfo.modals.unsavedChangesError.description'),
        primaryAction: {
          label: i18n.t('AccountInformationPersonalInfo.modals.unsavedChangesError.keepEditingButton'),
          onPress: () => {
            store.dispatch({
              type: CLOSE_MODAL
            });
          }
        },
        secondaryAction: {
          label: i18n.t('AccountInformationPersonalInfo.modals.unsavedChangesError.discardChangesButton'),
          onPress: () => {
            notifyApp('adc-webview:navigate-back-app');
          },
          destructive: true
        },
        buttonLayout: 'column'
      }
    });
  };
  const onBack = useCallback((initialValues, currentValues) => {
    if (hasFormChanged(initialValues, currentValues)) {
      showUnsavedChangesModal();
      return;
    }
    notifyApp('adc-webview:navigate-back-app');
  }, [hasFormChanged]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, isMinor ? /*#__PURE__*/React.createElement(AccountInfoMinor, Object.assign({
    patient: patient,
    onSave: onSave,
    onBack: onBack,
    hasFormChanged: hasFormChanged,
    validateDOBAndDisplayError: validateDOBAndDisplayError,
    validateEntriesLengthAndDisplayError: validateEntriesLengthAndDisplayError,
    loading: loading,
    showPhoneInput: showPhoneInput,
    phoneErrorTimestamp: phoneErrorTimestamp
  }, rest, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 247,
      columnNumber: 9
    }
  })) : /*#__PURE__*/React.createElement(AccountInfoAdult, Object.assign({
    patient: patient,
    app: app,
    onSave: onSave,
    onBack: onBack,
    hasFormChanged: hasFormChanged,
    loading: loading,
    minAge: minAge,
    showPhoneInput: showPhoneInput,
    validateDOBAndDisplayError: validateDOBAndDisplayError,
    validateEntriesLengthAndDisplayError: validateEntriesLengthAndDisplayError,
    validateIsMinor: validateIsMinor,
    showMinorRestrictionMessage: showMinorRestrictionMessage,
    phoneErrorTimestamp: phoneErrorTimestamp
  }, rest, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 260,
      columnNumber: 9
    }
  })));
};
export default connector(AccountInformation);